import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  FileUp,
  Pill,
  ShieldCheck,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { mergePatientUploadFlowState, persistPatientUploadFlowState } from "../../utils/patientUploadFlowSession";
import { readRequestDraft, saveRequestDraft } from "./requestDraftSession";
import { apiRequest } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { runPresignedUploadWithRollback } from "../../utils/prescriptionUploadFlow";
import "./RequestFlow.scss";
import WizardSteps from "./WizardSteps";

function guessKind(name) {
  const lower = String(name || "").toLowerCase();
  if (lower.includes("prescription") || lower.includes("rx")) return "prescription";
  if (lower.includes("insurance") || lower.includes("policy")) return "insurance";
  if (
    lower.includes("report") ||
    lower.includes("mri") ||
    lower.includes("xray") ||
    lower.includes("scan")
  ) {
    return "report";
  }
  return "other";
}

function isImagePreviewable(entry) {
  const f = entry?.file;
  if (!(f instanceof File)) return false;
  const mime = String(f.type || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  const n = String(entry?.name || f.name || "").toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(n);
}

function truncateFileName(name, max = 34) {
  const s = String(name || "");
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
}

function formatFileKb(size) {
  const n = Number(size);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const kb = Math.max(1, Math.round(n / 1024));
  return `${kb} KB`;
}

/**
 * Continues the legacy three-step uploader (prescriptions → reports → insurance) with
 * `linearMatchRequestId` in state, then returns to this flow for category and review.
 */
export default function RequestUploadEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loc = useLocation();
  const pickPrescriptionRef = useRef(null);
  const pickReportRef = useRef(null);
  const pickInsuranceRef = useRef(null);
  const [pickedFiles, setPickedFiles] = useState([]);
  const [activeDocKind, setActiveDocKind] = useState("prescription");
  const [continueBusy, setContinueBusy] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  const flowState = useMemo(
    () => mergePatientUploadFlowState({ ...loc.state, linearMatchRequestId: id, startFlow: true }),
    [id, loc.state]
  );
  const [docTypes, setDocTypes] = useState({});
  const [uploadedFileIds, setUploadedFileIds] = useState([]);

  const toBackendType = (type) => {
    const t = String(type || "report").toLowerCase();
    if (t === "prescription") return "prescription";
    // Backend currently accepts only prescription/report for this endpoint.
    // Keep insurance as a UI category but register/upload it as report.
    return "report";
  };

  useEffect(() => {
    if (!id) return;
    const existing = readRequestDraft(id);
    if (Array.isArray(existing.files) && existing.files.length) {
      setPickedFiles(existing.files);
    }
    if (existing.docTypes && typeof existing.docTypes === "object") {
      setDocTypes(existing.docTypes);
    }
    if (Array.isArray(existing.uploadedFileIds)) {
      setUploadedFileIds(existing.uploadedFileIds);
    }
  }, [id]);

  const fileSummary = useMemo(() => {
    const count = pickedFiles.length;
    if (!count) return "Prescriptions, scans, lab reports, and insurance papers. PDF, JPG or PNG.";
    const totalBytes = pickedFiles.reduce((sum, file) => sum + (file.size || 0), 0);
    const totalMb = (totalBytes / (1024 * 1024)).toFixed(1);
    return `${count} file${count > 1 ? "s" : ""} selected (${totalMb} MB total).`;
  }, [pickedFiles]);

  const hasPersistedMatchRequestId = Boolean(id && /^[a-f0-9]{24}$/i.test(String(id)));

  const fileCounts = useMemo(() => {
    const out = { prescription: 0, report: 0, insurance: 0, other: 0 };
    pickedFiles.forEach((f) => {
      const kind = String(docTypes[f.id] || f.kind || "other");
      if (out[kind] == null) out.other += 1;
      else out[kind] += 1;
    });
    return out;
  }, [pickedFiles, docTypes]);

  const kindPeek = useMemo(() => {
    const names = { prescription: [], report: [], insurance: [] };
    const imageEntries = { prescription: [], report: [], insurance: [] };
    pickedFiles.forEach((f) => {
      const kind = String(docTypes[f.id] || f.kind || "other");
      if (!names[kind]) return;
      names[kind].push(f.name || "File");
      if (imageEntries[kind].length < 2 && f.file instanceof File && isImagePreviewable(f)) {
        imageEntries[kind].push(f);
      }
    });
    return { names, imageEntries };
  }, [pickedFiles, docTypes]);

  const openPickerForKind = (kind) => {
    setActiveDocKind(kind);
    if (kind === "prescription") pickPrescriptionRef.current?.click();
    else if (kind === "report") pickReportRef.current?.click();
    else if (kind === "insurance") pickInsuranceRef.current?.click();
  };

  const normalizeSelectedFiles = (fileList, forcedKind) => {
    const files = Array.from(fileList || []);
    const accepted = files.filter((f) => {
      const mime = String(f.type || "").toLowerCase();
      const name = String(f.name || "").toLowerCase();
      return mime.includes("pdf") || mime.includes("png") || mime.includes("jpeg") || name.endsWith(".pdf") || name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg");
    });
    const mapped = accepted.map((f) => ({
      id: `${f.name}-${f.lastModified}-${f.size}`,
      name: f.name,
      size: f.size || 0,
      mime: f.type || "",
      kind: forcedKind || guessKind(f.name),
      file: f,
    }));
    const typeMap = {};
    mapped.forEach((f) => {
      typeMap[f.id] = f.kind;
    });
    setDocTypes((prev) => ({ ...prev, ...typeMap }));
    setPickedFiles((prev) => {
      const existing = new Set(prev.map((f) => f.id));
      const next = mapped.filter((f) => !existing.has(f.id));
      return [...prev, ...next];
    });
  };

  const uploadSelectedFiles = async () => {
    if (!id) return;
    const uploadedSet = new Set(uploadedFileIds);
    const toUpload = pickedFiles.filter((f) => f?.file instanceof File && !uploadedSet.has(f.id));
    if (!toUpload.length) return;
    const selectedItems = toUpload.map((f) => ({
      type: toBackendType(docTypes[f.id] || f.kind || "report"),
      file: f.file,
    }));

    const requestUploadUrls = async (items) =>
      apiRequest({
        method: "POST",
        url: ENDPOINTS.PRESCRIPTION_UPLOAD_URLS,
        data: {
          files: items.map((item) => ({
            type: item.type,
            filename: item.file?.name,
          })),
        },
      });

    const getUploadsFromResponse = (res) => {
      const payload = res?.data || res || {};
      return payload?.uploads || payload?.items || payload?.data?.uploads || payload?.data?.items || [];
    };

    const uploadFileToS3 = async (uploadUrl, file) => {
      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!response.ok) {
        throw new Error(`Failed to upload ${file.name} to storage`);
      }
    };

    const submitPrescriptionItems = async (items) =>
      apiRequest({
        method: "POST",
        url: ENDPOINTS.UPLOAD_PRESCRIPTIONS,
        data: {
          items,
          matchRequestId: String(id),
        },
      });

    const uploadViaMultipartFallback = async (items) => {
      const formData = new FormData();
      formData.append("matchRequestId", String(id));
      items.forEach((it) => {
        const field =
          it.type === "prescription"
            ? "prescription"
            : "report";
        formData.append(field, it.file, it.file?.name || "file");
      });
      return apiRequest({
        method: "POST",
        url: ENDPOINTS.UPLOAD_PRESCRIPTIONS,
        data: formData,
      });
    };

    try {
      await runPresignedUploadWithRollback({
        apiRequest,
        requestUploadUrls,
        getUploadsFromResponse,
        uploadFileToS3,
        submitPrescriptionItems,
        selectedItems,
      });
    } catch {
      await uploadViaMultipartFallback(selectedItems);
    }
    const mergedIds = Array.from(new Set([...uploadedFileIds, ...toUpload.map((f) => f.id)]));
    setUploadedFileIds(mergedIds);
    saveRequestDraft(id, { uploadedFileIds: mergedIds });
  };

  const getLinkedDocsCount = async () => {
    if (!id) return 0;
    const res = await apiRequest({
      method: "GET",
      url: ENDPOINTS.GET_MATCH_REQUEST_DETAIL(String(id)),
    });
    const docs = Array.isArray(res?.request?.requestDocuments) ? res.request.requestDocuments : [];
    return docs.length;
  };

  const confirmUploadAndProceed = async () => {
    if (!id) return;
    setContinueBusy(true);
    setUploadError("");
    try {
      await uploadSelectedFiles();
      const linkedDocs = await getLinkedDocsCount();
      if (!linkedDocs) {
        setUploadError("Files are not linked yet. Please upload again and wait a moment.");
        return;
      }
      const merged = flowState;
      persistPatientUploadFlowState(merged);
      const serializableFiles = pickedFiles.map((f) => ({
        id: f.id,
        name: f.name,
        size: f.size,
        mime: f.mime,
        kind: docTypes[f.id] || f.kind || "other",
      }));
      saveRequestDraft(id, { files: serializableFiles, docTypes });
      setReviewModalOpen(false);
      navigate(`/request/${id}/category`, {
        state: {
          ...merged,
          linearMatchRequestId: id,
          fromRequestFlow: true,
        },
      });
    } catch (e) {
      setUploadError(e?.message || "Could not upload files. Please try again.");
    } finally {
      setContinueBusy(false);
    }
  };

  const handleContinueClick = () => {
    if (!pickedFiles.length || continueBusy) return;
    setUploadError("");
    setReviewModalOpen(true);
  };

  const closeReviewModal = useCallback(() => {
    if (continueBusy) return;
    setReviewModalOpen(false);
    setUploadError("");
  }, [continueBusy]);

  const removeFileFromReview = useCallback((fileId) => {
    setPickedFiles((prev) => prev.filter((f) => f.id !== fileId));
    setDocTypes((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  }, []);

  useEffect(() => {
    if (!reviewModalOpen) return;
    if (pickedFiles.length === 0) {
      setReviewModalOpen(false);
      setUploadError("");
    }
  }, [reviewModalOpen, pickedFiles.length]);

  const changeDocTypeInReview = useCallback((fileId, nextKind) => {
    setDocTypes((prev) => ({ ...prev, [fileId]: nextKind }));
    setPickedFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, kind: nextKind } : f))
    );
  }, []);

  return (
    <>
    <section className="reqWizard">
      <div className="reqWizard__card">
        <p className="reqWizard__eyebrow">New care request</p>
        <h1 className="reqWizard__title">Tell us what you need</h1>
        <p className="reqWizard__subtitle">
          Upload documents and pick the exact procedure. Doctors will bid once verified.
        </p>

        <WizardSteps active={0} />

        {hasPersistedMatchRequestId ? (
          <p className="reqWizard__sectionSub reqWizard__reviewQueueHint" role="note">
            New or replaced uploads on this care request go through clinical review again. Bidding stays paused
            until all linked documents are approved.
          </p>
        ) : null}

        <div className="reqWizard__section">
          <h2 className="reqWizard__sectionTitle">Upload your documents</h2>
          <p className="reqWizard__sectionSub">{fileSummary}</p>
          <div className="reqWizard__docTypes">
            <div
              className="reqWizard__docTypeWrap"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                normalizeSelectedFiles(e.dataTransfer?.files, "prescription");
              }}
            >
              <DocumentTypeCard
                kind="prescription"
                title="Prescriptions"
                subtitle="Doctor prescription, Rx notes"
                count={fileCounts.prescription}
                peekNames={kindPeek.names.prescription}
                imageEntries={kindPeek.imageEntries.prescription}
                active={activeDocKind === "prescription"}
                icon={<Pill size={16} />}
                onSelect={() => setActiveDocKind("prescription")}
                onPickFiles={() => openPickerForKind("prescription")}
              />
            </div>
            <div
              className="reqWizard__docTypeWrap"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                normalizeSelectedFiles(e.dataTransfer?.files, "report");
              }}
            >
              <DocumentTypeCard
                kind="report"
                title="Medical reports"
                subtitle="Lab, scan, MRI, X-ray reports"
                count={fileCounts.report}
                peekNames={kindPeek.names.report}
                imageEntries={kindPeek.imageEntries.report}
                active={activeDocKind === "report"}
                icon={<FileUp size={16} />}
                onSelect={() => setActiveDocKind("report")}
                onPickFiles={() => openPickerForKind("report")}
              />
            </div>
            <div
              className="reqWizard__docTypeWrap"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                normalizeSelectedFiles(e.dataTransfer?.files, "insurance");
              }}
            >
              <DocumentTypeCard
                kind="insurance"
                title="Insurance papers"
                subtitle="Policy, card, TPA approvals"
                count={fileCounts.insurance}
                peekNames={kindPeek.names.insurance}
                imageEntries={kindPeek.imageEntries.insurance}
                active={activeDocKind === "insurance"}
                icon={<ShieldCheck size={16} />}
                onSelect={() => setActiveDocKind("insurance")}
                onPickFiles={() => openPickerForKind("insurance")}
              />
            </div>
          </div>
          <input
            ref={pickPrescriptionRef}
            className="reqWizard__picker"
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={(e) => {
              normalizeSelectedFiles(e.target.files, "prescription");
              e.target.value = "";
            }}
          />
          <input
            ref={pickReportRef}
            className="reqWizard__picker"
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={(e) => {
              normalizeSelectedFiles(e.target.files, "report");
              e.target.value = "";
            }}
          />
          <input
            ref={pickInsuranceRef}
            className="reqWizard__picker"
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={(e) => {
              normalizeSelectedFiles(e.target.files, "insurance");
              e.target.value = "";
            }}
          />
          <p className="reqWizard__sectionSub">
            Use Add files on each category to browse, or drop files onto that row. PDF, JPG, or PNG — up to 25 MB
            each. Tap a category to highlight it before adding more files.
          </p>

          {uploadError ? (
            <p className="reqReview__p reqReview__p--err" style={{ marginTop: 10 }}>{uploadError}</p>
          ) : null}
        </div>

        <div className="reqWizard__actions reqWizard__actions--primaryOnly">
          <button
            type="button"
            className="reqWizard__primary"
            onClick={handleContinueClick}
            disabled={!pickedFiles.length || continueBusy}
          >
            Continue
            <ArrowRight size={16} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </section>
    <UploadReviewModal
      open={reviewModalOpen}
      onClose={closeReviewModal}
      files={pickedFiles}
      docTypes={docTypes}
      busy={continueBusy}
      error={uploadError}
      onConfirm={() => void confirmUploadAndProceed()}
      onRemoveFile={removeFileFromReview}
      onChangeDocType={changeDocTypeInReview}
    />
    </>
  );
}

function DocKindMiniThumb({ entry }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!isImagePreviewable(entry)) {
      setUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(entry.file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [entry]);

  if (!url) return null;
  return <img src={url} alt="" className="reqWizard__docTypeThumb" decoding="async" />;
}

function DocumentTypeCard({
  kind,
  title,
  subtitle,
  count,
  peekNames = [],
  imageEntries = [],
  active,
  onSelect,
  onPickFiles,
  icon,
}) {
  const hasFiles = count > 0;
  const showPeek = hasFiles && peekNames.length > 0;

  return (
    <div
      className={`reqWizard__docTypeCard${active ? " is-active" : ""}${hasFiles ? " reqWizard__docTypeCard--hasFiles" : ""}`}
    >
      {hasFiles ? (
        <span className="reqWizard__docTypeOk" aria-hidden>
          <Check size={12} strokeWidth={3} />
        </span>
      ) : null}
      <button
        type="button"
        className="reqWizard__docTypeSelect"
        onClick={onSelect}
        aria-pressed={active}
        aria-label={`${title}${hasFiles ? `, ${count} file${count === 1 ? "" : "s"}` : ", no files yet"}. Select to review.`}
      >
        <span className="reqWizard__docTypeHead">
          <span className="reqWizard__docTypeIcon">{icon}</span>
          <span className="reqWizard__docTypeTitle">{title}</span>
        </span>
        <span className="reqWizard__docTypeSub">{subtitle}</span>
        {imageEntries.length > 0 ? (
          <span className="reqWizard__docTypeThumbs" aria-hidden>
            {imageEntries.map((e) => (
              <span key={e.id} className="reqWizard__docTypeThumbWrap">
                <DocKindMiniThumb entry={e} />
              </span>
            ))}
          </span>
        ) : null}
        <span className="reqWizard__docTypeCountRow">
          <span className="reqWizard__docTypeCount">{count} file{count === 1 ? "" : "s"}</span>
        </span>
        {showPeek ? (
          <span className="reqWizard__docTypePeek" title={peekNames.join(", ")}>
            {peekNames.slice(0, 2).map((n, i) => (
              <span key={`${n}-${i}`} className="reqWizard__docTypePeekName">
                {truncateFileName(n)}
              </span>
            ))}
            {peekNames.length > 2 ? (
              <span className="reqWizard__docTypePeekMore">+{peekNames.length - 2} more</span>
            ) : null}
          </span>
        ) : null}
      </button>
      <div className="reqWizard__docTypeFooter">
        <button
          type="button"
          className="reqWizard__docTypeUploadBtn"
          onClick={(e) => {
            e.preventDefault();
            onPickFiles();
          }}
          aria-label={`Add files to ${title}`}
        >
          <Upload size={15} strokeWidth={2.25} aria-hidden />
          <span>Add files</span>
        </button>
      </div>
      <span className="reqWizard__docTypeKind">{kind}</span>
    </div>
  );
}

/** Large slide preview inside review modal (blob URL for images, icon for PDFs). */
function ModalReviewSlidePreview({ entry }) {
  const [url, setUrl] = useState(null);
  const isImg = entry.file instanceof File && isImagePreviewable(entry);

  useEffect(() => {
    if (!isImg) {
      setUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(entry.file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [entry.id, entry.file, isImg]);

  if (!isImg) {
    return (
      <div className="reqUploadReviewModal__slideInner reqUploadReviewModal__slideInner--doc">
        <FileText size={52} strokeWidth={1.25} aria-hidden />
        <span className="reqUploadReviewModal__slideDocLabel">PDF or document</span>
      </div>
    );
  }

  return (
    <div className={`reqUploadReviewModal__slideInner${url ? "" : " reqUploadReviewModal__slideInner--loading"}`} aria-busy={!url}>
      {url ? (
        <img
          src={url}
          alt={entry.name || "Document preview"}
          className="reqUploadReviewModal__slideImg"
          decoding="async"
        />
      ) : null}
    </div>
  );
}

function UploadReviewModal({ open, onClose, files, docTypes, busy, error, onConfirm, onRemoveFile, onChangeDocType }) {
  const sliderRef = useRef(null);
  const wasOpenRef = useRef(false);
  const [slideIndex, setSlideIndex] = useState(0);

  const scrollToIndex = useCallback(
    (i) => {
      const el = sliderRef.current;
      if (!el || !files.length) return;
      const idx = Math.max(0, Math.min(files.length - 1, i));
      const w = el.clientWidth || 1;
      el.scrollTo({ left: idx * w, behavior: "smooth" });
      setSlideIndex(idx);
    },
    [files.length]
  );

  const goPrev = useCallback(() => {
    const el = sliderRef.current;
    if (!el || !files.length) return;
    const w = el.clientWidth || 1;
    const idx = Math.round(el.scrollLeft / w);
    scrollToIndex(idx - 1);
  }, [files.length, scrollToIndex]);

  const goNext = useCallback(() => {
    const el = sliderRef.current;
    if (!el || !files.length) return;
    const w = el.clientWidth || 1;
    const idx = Math.round(el.scrollLeft / w);
    scrollToIndex(idx + 1);
  }, [files.length, scrollToIndex]);

  const onSliderScroll = useCallback(() => {
    const el = sliderRef.current;
    if (!el || !files.length) return;
    const w = el.clientWidth || 1;
    const idx = Math.round(el.scrollLeft / w);
    setSlideIndex(Math.max(0, Math.min(files.length - 1, idx)));
  }, [files.length]);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (files.length === 0) return;
    const el = sliderRef.current;
    const w = el?.clientWidth || 1;
    if (!wasOpenRef.current) {
      setSlideIndex(0);
      requestAnimationFrame(() => {
        sliderRef.current?.scrollTo({ left: 0, behavior: "auto" });
      });
      wasOpenRef.current = true;
    } else {
      setSlideIndex((prev) => {
        const next = Math.min(prev, files.length - 1);
        requestAnimationFrame(() => {
          sliderRef.current?.scrollTo({ left: next * w, behavior: "auto" });
        });
        return next;
      });
    }
  }, [open, files.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) {
        onClose();
        return;
      }
      if (busy || files.length <= 1) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, busy, files.length, goPrev, goNext]);

  if (!open || typeof document === "undefined") return null;
  if (!files.length) return null;

  const current = files[slideIndex] || files[0];
  const multi = files.length > 1;
  const currentKind = String(docTypes[current.id] || current.kind || "other");

  return createPortal(
    <div className="reqUploadReviewModal" role="dialog" aria-modal="true" aria-labelledby="req-upload-review-title">
      <button
        type="button"
        className="reqUploadReviewModal__backdrop"
        aria-label="Close dialog"
        onClick={() => {
          if (!busy) onClose();
        }}
        disabled={busy}
      />
      <div className="reqUploadReviewModal__panel">
        <header className="reqUploadReviewModal__header">
          <div className="reqUploadReviewModal__headerText">
            <span className="reqUploadReviewModal__eyebrow">Quick check</span>
            <h2 id="req-upload-review-title" className="reqUploadReviewModal__title">
              Review documents
            </h2>
          </div>
          <button
            type="button"
            className="reqUploadReviewModal__close"
            onClick={() => {
              if (!busy) onClose();
            }}
            disabled={busy}
            aria-label="Close"
          >
            <X size={20} strokeWidth={2.25} />
          </button>
        </header>

        <div className="reqUploadReviewModal__body">
        <p className="reqUploadReviewModal__lead">
          {multi
            ? "Swipe the preview or use the arrows. Adjust type or remove a file, then confirm."
            : "Confirm the type or remove the file, then continue."}
        </p>

        <div className="reqUploadReviewModal__sliderWrap">
          {multi ? (
            <button
              type="button"
              className="reqUploadReviewModal__navBtn reqUploadReviewModal__navBtn--prev"
              onClick={goPrev}
              disabled={busy || slideIndex <= 0}
              aria-label="Previous document"
            >
              <ChevronLeft size={28} strokeWidth={2} />
            </button>
          ) : null}
          <div
            ref={sliderRef}
            className="reqUploadReviewModal__slider"
            onScroll={onSliderScroll}
            role="region"
            aria-roledescription="carousel"
            aria-label="Document previews"
          >
            {files.map((file, i) => (
              <div
                key={file.id}
                className="reqUploadReviewModal__slide"
                aria-hidden={i !== slideIndex}
                id={`req-upload-slide-${i}`}
              >
                <ModalReviewSlidePreview entry={file} />
              </div>
            ))}
          </div>
          {multi ? (
            <button
              type="button"
              className="reqUploadReviewModal__navBtn reqUploadReviewModal__navBtn--next"
              onClick={goNext}
              disabled={busy || slideIndex >= files.length - 1}
              aria-label="Next document"
            >
              <ChevronRight size={28} strokeWidth={2} />
            </button>
          ) : null}
        </div>

        {multi ? (
          <div className="reqUploadReviewModal__dots" role="tablist" aria-label="Choose document">
            {files.map((file, i) => (
              <button
                key={file.id}
                type="button"
                role="tab"
                aria-selected={i === slideIndex}
                aria-controls={`req-upload-slide-${i}`}
                className={`reqUploadReviewModal__dot${i === slideIndex ? " is-active" : ""}`}
                onClick={() => scrollToIndex(i)}
                disabled={busy}
                aria-label={`Show document ${i + 1} of ${files.length}`}
              />
            ))}
          </div>
        ) : null}

        {current ? (
          <div className="reqUploadReviewModal__currentMeta">
            <div className="reqUploadReviewModal__metaTop">
              <p className="reqUploadReviewModal__currentName" title={current.name}>
                {truncateFileName(current.name || "File", 42)}
              </p>
              <div className="reqUploadReviewModal__metaTopRight">
                {multi ? (
                  <span className="reqUploadReviewModal__counter" aria-live="polite">
                    {slideIndex + 1}/{files.length}
                  </span>
                ) : null}
                <span className="reqUploadReviewModal__currentSize">{formatFileKb(current.size)}</span>
              </div>
            </div>
            <div className="reqUploadReviewModal__typeBlock">
              <label className="reqUploadReviewModal__fieldLabel" htmlFor={`req-upload-type-${current.id}`}>
                Document type
              </label>
              <div className="reqUploadReviewModal__typeRow">
                <select
                  id={`req-upload-type-${current.id}`}
                  className="reqUploadReviewModal__typeSelect"
                  value={currentKind}
                  onChange={(e) => onChangeDocType(current.id, e.target.value)}
                  disabled={busy}
                >
                  <option value="prescription">Prescription</option>
                  <option value="report">Medical report</option>
                  <option value="insurance">Insurance</option>
                  <option value="other">Other</option>
                </select>
                <button
                  type="button"
                  className="reqUploadReviewModal__deleteBtn"
                  onClick={() => onRemoveFile(current.id)}
                  disabled={busy}
                  aria-label={`Remove ${current.name || "file"}`}
                >
                  <Trash2 size={15} strokeWidth={1.85} aria-hidden />
                  <span className="reqUploadReviewModal__deleteBtnLabel">Remove</span>
                </button>
              </div>
            </div>
          </div>
        ) : null}

        </div>

        {error ? <p className="reqUploadReviewModal__error">{error}</p> : null}
        <div className="reqUploadReviewModal__footer">
          <button type="button" className="reqUploadReviewModal__cancel" onClick={() => !busy && onClose()} disabled={busy}>
            Go back
          </button>
          <button type="button" className="reqUploadReviewModal__confirm" onClick={onConfirm} disabled={busy || !files.length}>
            {busy ? "Uploading…" : "Confirm & continue"}
            {!busy ? <ArrowRight size={16} strokeWidth={2.4} aria-hidden /> : null}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
